import React from 'react';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable } from 'mobx';
import { Link } from 'react-router-dom';
import { Button, Space, Spin, Table, Tag, Typography, notification, Popconfirm } from 'antd';
import { TemplatesProxy } from '../services/catalogServiceAPI/templatesProxy';
import { ChallengesProxy } from '../services/catalogServiceAPI/challengesProxy';
import { config } from '../../config';

@observer
export class Templates extends React.Component {
    templatesProxy = new TemplatesProxy();
    challengesProxy = new ChallengesProxy();

    @observable templates = [];
    @observable challenges = [];
    @observable isLoading = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @action.bound setIsLoading = (v) => this.isLoading = v;
    @action.bound setTemplates = (v) => this.templates.replace(v);
    @action.bound setChallenges = (v) => this.challenges.replace(v);

    @computed get challengesByTemplateId() {
        const map = new Map();
        for (const c of this.challenges) {
            const tid = c.roomRequirements?.templateId;
            if (!tid) continue;
            if (!map.has(tid)) map.set(tid, []);
            map.get(tid).push(c);
        }
        return map;
    }

    componentDidMount() {
        this.fetch();
    }

    async fetch() {
        this.setIsLoading(true);
        try {
            const [templates, challenges] = await Promise.all([
                this.templatesProxy.list(),
                this.challengesProxy.list().catch(() => []),
            ]);
            this.setTemplates(templates);
            this.setChallenges(challenges);
        } catch (e) {
            notification.error({ message: 'Failed to load templates', description: e.message });
        } finally {
            this.setIsLoading(false);
        }
    }

    openCreate = () => {
        window.open(`${config().clientBaseUrl}/editor?mode=template`, '_blank', 'noopener,noreferrer');
    }

    openUpdate = (id) => {
        window.open(`${config().clientBaseUrl}/editor?mode=template&templateId=${id}`, '_blank', 'noopener,noreferrer');
    }

    remove = async (id) => {
        try {
            await this.templatesProxy.remove(id);
            notification.success({ message: 'Template deleted' });
            this.fetch();
        } catch (e) {
            notification.error({ message: 'Failed to delete', description: e.message });
        }
    }

    columns = [
        { title: 'Name', dataIndex: 'name' },
        {
            title: 'Used in challenges',
            width: 280,
            render: (_, row) => {
                const challenges = this.challengesByTemplateId.get(row.id) || [];
                if (!challenges.length) return <Typography.Text type="secondary">—</Typography.Text>;
                return (
                    <Space direction="vertical" size={4}>
                        {challenges.map(c => (
                            <Link key={c.id} to={`/challenges/edit/${c.id}`}>
                                <Tag color="blue" style={{ cursor: 'pointer' }}>{c.title || c.id}</Tag>
                            </Link>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            width: 180,
            render: (v) => v ? new Date(v).toLocaleString() : '—',
        },
        {
            title: 'Updated',
            dataIndex: 'updatedAt',
            width: 180,
            render: (v) => v ? new Date(v).toLocaleString() : '—',
        },
        {
            title: 'Actions',
            width: 200,
            render: (_, row) => (
                <Space>
                    <Button size="small" type="primary" onClick={() => this.openUpdate(row.id)}>Update</Button>
                    <Popconfirm title="Delete this template?" onConfirm={() => this.remove(row.id)}>
                        <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    render() {
        return (
            <Space direction="vertical" size={16} style={{ width: '100%', padding: '24px 32px' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Typography.Title level={3} style={{ margin: 0 }}>Templates</Typography.Title>
                    <Button type="primary" onClick={this.openCreate}>Create new</Button>
                </Space>
                {this.isLoading ? <Spin /> : (
                    <Table
                        rowKey="id"
                        dataSource={this.templates.slice()}
                        columns={this.columns}
                        pagination={{ pageSize: 20 }}
                    />
                )}
            </Space>
        );
    }
}
